import React, { FC, useState } from 'react';
import { Button, Modal } from 'antd';
import copy from 'copy-to-clipboard';
import { Close, Copy, Download } from 'common/svg';
import { dateFormatter } from 'common/helpers/date.helper';
import { priceFormatter } from 'common/helpers/format.helper';
import { DataTable } from 'common/components/Table/DataTable';
import { nameBuilder } from 'common/helpers/name.helper';
import {
  EPurchaseOrderStatus,
  IPurchaseOrderItem,
  IPurchaseOrderModel,
  getPurchaseOrderItemsConfig,
} from 'entities/PurchaseOrder/PurchaseOrder.models';
import { PurchaseOrderStatus } from 'entities/PurchaseOrder/components/PurchaseOrderStatus';
import { purchaseOrderNumberBuilder } from 'entities/PurchaseOrder/PurchaseOrder.helper';
import { exportPurchaseOrderToExcel } from 'entities/PurchaseOrder/PurchaseOrder.export';
import { IUserModel } from 'entities/User/User.models';

interface IComponentProps {
  open: boolean;
  order: IPurchaseOrderModel | null;
  franchisee: IUserModel | null;
  onCancel: () => void;
}

export const PurchaseOrderModal: FC<IComponentProps> = ({ open, order, franchisee, onCancel }) => {
  const [exporting, setExporting] = useState(false);
  const items = order?.items.map((item, index) => ({ ...item, key: index }));
  const franchiseeName = nameBuilder(franchisee?.firstName, franchisee?.lastName);

  const copySonum = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (order) {
      copy(order.sonum);
    }
  };

  const handleExport = async () => {
    if (!order) {
      return;
    }

    setExporting(true);

    try {
      await exportPurchaseOrderToExcel(order, franchiseeName);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal
      open={open}
      rootClassName="modal"
      width={720}
      height={600}
      centered
      title="Order Information"
      closeIcon={<Close className="icon-gray" />}
      classNames={{ body: 'scrollbar-y' }}
      onCancel={onCancel}
      footer={
        <div className="flex flex-justify-between">
          <Button className="btn-secondary" icon={<Download className="icon-gray" />} loading={exporting} onClick={handleExport}>
            Export Excel
          </Button>
          <Button className="btn-primary" onClick={onCancel}>
            Back
          </Button>
        </div>
      }
    >
      <div className="mb-2 flex">
        <div className="flex__item_1">
          <div className="mb-5 ff-medium fs-xxs">Franchisee Details</div>

          <div className="mb-4">
            <div className="mb-2 ff-regular fs-xxxs color-gray-1">Name</div>
            <span className="ff-regular fs-xxs">{franchiseeName}</span>
          </div>
        </div>

        <div className="flex__item_1">
          <div className="mb-5 ff-medium fs-xxs">Payment Details</div>

          {items && (
            <div className="mb-4">
              <div className="mb-2 ff-regular fs-xxxs color-gray-1">Items</div>
              <span className="ff-regular fs-xxs">{items.length}</span>
            </div>
          )}

          {order && (
            <div>
              <div className="mb-2 ff-regular fs-xxxs color-gray-1">Amount (Total)</div>
              <span className="ff-regular fs-xxs">{priceFormatter(order.amount)}</span>
            </div>
          )}
        </div>

        <div className="flex__item_1">
          <div className="mb-5 ff-medium fs-xxs">Order Details</div>

          {order?.orderedAt && (
            <div className="mb-4">
              <div className="mb-2 ff-regular fs-xxxs color-gray-1">Ordered</div>
              <span className="ff-regular fs-xxs">{dateFormatter(order.orderedAt, 'MM/DD/YYYY HH:mm')}</span>
            </div>
          )}

          {order?.deliveredAt && order.status === EPurchaseOrderStatus.Delivered && (
            <div className="mb-4">
              <div className="mb-2 ff-regular fs-xxxs color-gray-1">Delivered</div>
              <span className="ff-regular fs-xxs">{dateFormatter(order.deliveredAt, 'MM/DD/YYYY HH:mm')}</span>
            </div>
          )}
        </div>

        <div className="flex__item_1 flex flex__column flex-align-end">
          {order?.status && (
            <div className="mb-5">
              <PurchaseOrderStatus status={order.status} />
            </div>
          )}

          {order && (
            <div className="flex">
              <span className="ff-medium fs-xxs">{purchaseOrderNumberBuilder(order.sonum)}</span>
              <Copy className="icon-gray" onClick={copySonum} />
            </div>
          )}

          {order?.drNumber && order.status === EPurchaseOrderStatus.Delivered && (
            <div className="mt-2 flex">
              <span className="ff-medium fs-xxs">{`DR#${order.drNumber}`}</span>
            </div>
          )}
        </div>
      </div>

      <div>
        {/* Delivery Receipt exists only for delivered orders; any other status shows the sales order view. */}
        <div className="mb-2 ff-medium fs-xxs">
          {order?.status === EPurchaseOrderStatus.Delivered ? 'Delivery Receipt Details' : 'Sales Order Details'}
        </div>

        <DataTable<IPurchaseOrderItem> dataSource={items || []} config={getPurchaseOrderItemsConfig(order?.status)} />
      </div>
    </Modal>
  );
};
